import JSON5 from "json5";
import { ScopeActions } from "./constants";

export const getExternalScopeActionName = (action: string) => {
  switch (action) {
    case ScopeActions.ENABLED_ACTION_READ:
      return "Read";
    case ScopeActions.ENABLED_ACTION_WRITE:
      return "Write";
    default:
      return action;
  }
};

export function capitalizeFirstLetter(stringToCapitalize: string): string {
  return stringToCapitalize.charAt(0).toUpperCase() + stringToCapitalize.slice(1);
}

export function firstLetterUpperCase(string: string) {
  if (string) {
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
  }
  return string;
}

export function snakeCaseToCamelCase(string: string) {
  if (string) {
    return string
      .split("_")
      .map((word) => {
        return firstLetterUpperCase(word);
      })
      .join("");
  }
  return string;
}

/**
 * Contains special translations for the spaced capitalization we should replace - word/phrase to search on
 * the left side, replacement on the right.
 */
const SPECIAL_CASED_WORDS: Record<string, string> = {
  oauth: "OAuth",
};

/**
 * Converts a `snake_case_string` to `Snake Case String`, making sure to "translate" any special words that
 * require a different capitalization than just uppercasing the first letter.
 */
export const snakeCaseToSpacedCapitalized = (text: string) => {
  return text
    .split("_")
    .map((word) => SPECIAL_CASED_WORDS[word] ?? capitalizeFirstLetter(word))
    .join(" ");
};

export function snakeCaseToSentenceCase(string: string) {
  if (string) {
    const sentenceCaseSnakeCase = firstLetterUpperCase(string);
    return sentenceCaseSnakeCase.replaceAll("_", " ").trim();
  }
  return string;
}

export function snakeCaseToTitleCase(string: string) {
  if (string) {
    const camelCaseString = snakeCaseToCamelCase(string);
    return camelCaseString.replace(/([A-Z])/g, " $1").trim();
  }
  return string;
}

export function stringRemoveSpaces(string: string) {
  if (string) {
    return string.replaceAll(" ", "");
  }
  return string;
}

export function formatBytes(bytes: number, decimals = 2) {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

export function getCursorPath(path: string) {
  const start_index = path.indexOf("cursor");
  if (start_index == -1) {
    return "";
  }

  const new_path = path.substring(start_index);
  return new_path;
}

export function isValidJSON(str: string) {
  try {
    JSON.parse(str);
  } catch {
    return false;
  }
  return true;
}

/**
 * Pretty-prints a JSON or JSON-like string with 2-space indentation. Falls back
 * to the more tolerant JSON5 parser (which accepts single-quoted keys/strings,
 * e.g. the single-quoted object literals the backend stores for some request
 * bodies), and finally to the original string when neither parser succeeds.
 * Note: JSON5 does not parse Python's True/False/None, so a dict repr
 * containing those still falls through to the raw string.
 */
export function prettyPrintJSONLike(str: string): string {
  try {
    return JSON.stringify(JSON.parse(str), null, 2);
  } catch {
    // Not strict JSON; try JSON5 below.
  }
  try {
    return JSON.stringify(JSON5.parse(str), null, 2);
  } catch {
    return str;
  }
}

export function isValidHostname(value: string): boolean {
  // Only ASCII letters, digits, hyphens, and dots. No @, /, \, etc.
  return /^[a-zA-Z0-9]([a-zA-Z0-9.-]*[a-zA-Z0-9])?$/.test(value);
}

export function removeValueFromStringArray(array: Array<string>, string: string) {
  const new_array = [...array];
  const index = new_array.indexOf(string);
  if (index !== -1) {
    new_array.splice(index, 1);
  }
  return new_array;
}

export function numberWithCommas(x: number | null) {
  return x ? x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") : "0";
}

export function convertStringToDate(date: string | null) {
  if (date) {
    return new Date(date);
  }
  return null;
}

export const replaceBackticksWithCode = (input?: string) => {
  return input?.replace(/`([^`]+)`/g, "<code>$1</code>");
};

export const formatValues = (values: string[]): string => {
  if (values.length === 0) {
    return "";
  } else if (values.length === 1) {
    return values[0];
  } else if (values.length === 2) {
    return `${values[0]} and ${values[1]}`;
  } else {
    const lastValue = values.pop();
    return `${values.join(", ")}, and ${lastValue}`;
  }
};
